import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import DashboardIcon from "@mui/icons-material/Dashboard"
import StorefrontIcon from "@mui/icons-material/Storefront"
import WarehouseIcon from "@mui/icons-material/Warehouse"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import PeopleAltIcon from "@mui/icons-material/PeopleAlt"
import AssessmentIcon from "@mui/icons-material/Assessment"
import { useNavigate } from "react-router-dom"

import MainLayout from "../../layouts/MainLayout"
import { appConfig, isDashboardVisibleInMenu } from "../../config/appConfig"

const dashboardIcons = {
  global: <DashboardIcon />,
  distributor: <StorefrontIcon />,
  relay: <StorefrontIcon />,
  agents: <PeopleAltIcon />,
  warehouses: <WarehouseIcon />,
  logistics: <LocalShippingIcon />,
  pro: <AssessmentIcon />,
  reporting: <AssessmentIcon />,
  activityLogs: <AssessmentIcon />,
}

function DashboardCard({ title, description, path, icon }) {
  const navigate = useNavigate()

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.4,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#1d4ed8",
            backgroundColor: "#dbeafe",
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography fontWeight={900}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            Module activé
          </Typography>
        </Box>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>

      <Button
        variant="contained"
        onClick={() => navigate(path)}
        sx={{
          alignSelf: "flex-start",
          textTransform: "none",
          fontWeight: 800,
        }}
      >
        Ouvrir
      </Button>
    </Paper>
  )
}

function DashboardPage() {
  const enabledDashboards = Object.entries(appConfig.dashboards || {})
    .filter(([key]) => isDashboardVisibleInMenu(key))
    .map(([key, dashboard]) => ({
      key,
      ...dashboard,
    }))

  return (
    <MainLayout>
      <Box>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Typography variant="h4" fontWeight={900}>
              Dashboard global
            </Typography>

            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {appConfig.description}
            </Typography>
          </Box>

          <Chip
            label={
              appConfig.dashboardMode === "single"
                ? "Mode client simple"
                : "Mode multi-organisation"
            }
            color={appConfig.dashboardMode === "single" ? "success" : "primary"}
            sx={{ fontWeight: 800, alignSelf: { xs: "flex-start", md: "center" } }}
          />
        </Stack>

        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          Les dashboards sont paramétrables selon le client. Pour une petite
          entreprise, on peut garder un seul dashboard. Pour une entreprise avec
          plusieurs organisations, on peut activer les dashboards séparés et une
          vue globale.
        </Alert>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          {enabledDashboards.map((dashboard) => (
            <DashboardCard
              key={dashboard.key}
              title={dashboard.label}
              description={dashboard.description}
              path={dashboard.path}
              icon={dashboardIcons[dashboard.key] || <DashboardIcon />}
            />
          ))}
        </Box>

        {enabledDashboards.length === 0 ? (
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography fontWeight={800}>Aucun dashboard activé.</Typography>
            <Typography variant="body2" color="text.secondary">
              Active au moins un dashboard dans frontend/src/config/appConfig.js.
            </Typography>
          </Paper>
        ) : null}
      </Box>
    </MainLayout>
  )
}

export default DashboardPage